import React from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSetup } from 'contexts/SetupContext'
import { SetupStepPopover } from './SetupStepPopover'
import { Check } from 'lucide-react'
import { SetupStepStatus, getSetupInProgressKey, STEPS } from 'utils/setupStorage'
import { setIsPersonalView } from 'redux/actions/feed'
import { RootState } from 'constants/interfaces'
import LocaleText from 'components/LocaleText'
import keys from 'constants/keys'

interface SetupStepItemProps {
  step: {
    id: number
    title: string
    description: string
    route: string
  }
  stepNumber: number
  status: SetupStepStatus
  isActive: boolean
  isPopoverOpen: boolean
  onOpenPopover: () => void
  onClosePopover: () => void
  totalSteps?: number
}

/**
 * SetupStepItem Component
 * Individual step in the setup timeline with popover interaction
 */
export const SetupStepItem: React.FC<SetupStepItemProps> = ({
  step,
  stepNumber,
  status,
  isActive,
  isPopoverOpen,
  onOpenPopover,
  onClosePopover,
  totalSteps,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { skipStep, minimizeDrawer, isStepCompleted, goToStep, completeStep } = useSetup()
  const activeGroupID = useSelector((state: RootState) => state.config.activeGroupID)
  const uid = useSelector((state: RootState) => state.auth.uid)

  /**
   * Handles "Get Started" action for a step
   * - Sets up in-progress flag for this specific user
   * - Navigates to the appropriate route
   * - Triggers the setup modal
   */
  const handleGetStarted = () => {
    if (!uid) return

    const setupInProgress = localStorage.getItem(getSetupInProgressKey(uid))

    if (!setupInProgress) {
      localStorage.setItem(getSetupInProgressKey(uid), 'true')
    }

    onClosePopover()
    minimizeDrawer()

    if (step.id === STEPS.FEED) {
      dispatch(setIsPersonalView(false))
    }
    // Step-specific logic
    if (step.id === STEPS.AI_FEED) {
      dispatch(setIsPersonalView(true))
    }

    // Determine target route based on step
    let targetRoute = step.route

    if (step.id === STEPS.REPORTS && activeGroupID) {
      targetRoute = `${keys.ROUTE_NAMES.REPORTS}/${activeGroupID}`
    }

    if (step.id === STEPS.RANKINGS_LIST) {
      targetRoute = keys.ROUTE_NAMES.RANKINGS_LIST
    }

    // Navigate to the target route
    if (targetRoute) {
      navigate(targetRoute)
    }
    if (step.id === STEPS.ORGANIZATION || step.id === STEPS.FIND_SALES_OPPORTUNITIES) {
      completeStep(step.id)
    }

    // Trigger modal after navigation
    if (!isStepCompleted(step.id) && step.id !== STEPS.ORGANIZATION && step.id !== STEPS.FIND_SALES_OPPORTUNITIES) {
      setTimeout(() => {
        const event = new CustomEvent('openSetupModal', {
          detail: { stepId: step.id, stepTitle: step.title },
        })
        window.dispatchEvent(event)
      }, 100)
    }
  }

  const handleSkip = () => {
    skipStep(step.id)
    onClosePopover()
  }

  /**
   * Handles step click
   * Prevents opening popover only for completed steps
   * Skipped steps can be reopened to complete them
   */
  const handleStepClick = () => {
    // Don't allow opening popover for completed steps
    if (status === 'completed') {
      return
    }
    goToStep(step.id)
    // Skipped steps can be reopened
    onOpenPopover()
  }

  const getStepIcon = () => {
    if (status === 'completed') {
      return <Check />
    }
    return null
  }

  return (
    <SetupStepPopover
      stepNumber={stepNumber}
      title={step.title}
      description={step.description}
      isOpen={isPopoverOpen}
      onClose={onClosePopover}
      onGetStarted={handleGetStarted}
      onSkip={handleSkip}
      totalSteps={totalSteps}
    >
      <StepContainer onClick={handleStepClick} isActive={isActive} isClickable={status !== 'completed'}>
        <StepIconContainer showLine={isPopoverOpen}>
          <StepCircle status={status} isActive={isActive}>
            {getStepIcon()}
          </StepCircle>
        </StepIconContainer>
        <StepTitle status={status}>
          <LocaleText text={step.title} isLocalUse={true} />
        </StepTitle>
      </StepContainer>
    </SetupStepPopover>
  )
}

const StepContainer = styled.div<{ isActive: boolean; isClickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: ${(props) => (props.isClickable !== false ? 'pointer' : 'default')};
  position: relative;
  z-index: ${(props) => (props.isActive ? '10' : '1')};
  transition: all 0.3s ease;
  min-width: 100px;
  opacity: ${(props) => (props.isClickable !== false ? '1' : '0.6')};

  &:hover {
    transform: ${(props) => (props.isClickable !== false ? 'translateY(-2px)' : 'none')};
  }
`

const StepIconContainer = styled.div<{ showLine: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  position: relative;

  /* Line to popover */
  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 3px;
    height: ${(props) => (props.showLine ? '60px' : '0')};
    background: #f005c5;
    transition: height 0.3s ease;
    z-index: 1;
  }
`

const StepCircle = styled.div<{ status: SetupStepStatus; isActive: boolean }>`
  width: ${(props) => (props.status === 'completed' || props.status === 'skipped' ? '20px' : '12px')};
  height: ${(props) => (props.status === 'completed' || props.status === 'skipped' ? '20px' : '12px')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  transition: all 0.3s ease;

  background: ${(props) => {
    if (props.status === 'completed') {
      return 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' // Green for completed
    }
    if (props.status === 'skipped') {
      return 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)' // Orange for skipped
    }
    if (props.isActive) {
      return '#f005c5'
    }
    return '#e2e8f0' // Gray for pending
  }};

  color: ${(props) => {
    if (props.status === 'completed' || props.status === 'skipped' || props.isActive) {
      return 'white'
    }
    return '#718096'
  }};

  box-shadow: ${(props) => {
    if (props.status === 'completed') {
      return '0 4px 12px rgba(72, 187, 120, 0.3)' // Green shadow for completed
    }
    if (props.status === 'skipped') {
      return '0 4px 12px rgba(237, 137, 54, 0.3)' // Orange shadow for skipped
    }
    if (props.isActive) {
      return '0 4px 12px rgba(102, 126, 234, 0.4)' // Purple shadow for active
    }
    return 'none'
  }};

  svg {
    width: 12px;
    height: 12px;
  }
`

const StepTitle = styled.span<{ status: SetupStepStatus }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => {
    if (props.status === 'completed') {
      return '#48bb78' // Green for completed
    }
    if (props.status === 'skipped') {
      return '#ed8936' // Orange for skipped
    }
    return '#2d3748' // Dark for pending/active
  }};
  text-align: center;
  line-height: 1.3;
  max-width: 120px;
  transition: color 0.3s ease;
`
